import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { UTCDateTimePicker } from '../components/ui/utc-datetime-picker';
import {
    Wrench, Plus, Search, Clock, CheckCircle, AlertTriangle, Calendar,
    FileText, Send, ShieldCheck, Plane, MapPin, User, Timer, Settings, Trash2, AlertOctagon, FileWarning
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lineTypes = [
    { value: 'preflight', label: 'Pre-vuelo' },
    { value: 'postflight', label: 'Post-vuelo' },
    { value: 'transit', label: 'Transito' },
    { value: 'troubleshooting', label: 'Troubleshooting' },
    { value: 'component_change', label: 'Cambio Componente' },
    { value: 'unscheduled', label: 'No Programado' },
];

const baseTypes = [
    { value: 'check_a', label: 'Check A' },
    { value: 'check_b', label: 'Check B' },
    { value: 'check_c', label: 'Check C' },
    { value: 'check_d', label: 'Check D' },
    { value: 'overhaul', label: 'Overhaul' },
    { value: 'structural', label: 'Reparacion Estructural' },
    { value: 'engine', label: 'Motor / APU' },
    { value: 'landing_gear', label: 'Tren de Aterrizaje' },
];

const statuses = [
    { value: 'pending', label: 'Pendiente', color: 'bg-amber-500/10 text-amber-400' },
    { value: 'in_progress', label: 'En Progreso', color: 'bg-sky-500/10 text-sky-400' },
    { value: 'completed', label: 'Completado', color: 'bg-emerald-500/10 text-emerald-400' },
    { value: 'deferred', label: 'Diferido', color: 'bg-red-500/10 text-red-400' },
];

const emptyLine = { aircraft_id: '', maintenance_type: 'preflight', description: '', ata_chapter: '', station: '', technician: '', work_order: '' };
const emptyBase = { aircraft_id: '', maintenance_type: 'check_a', description: '', ata_chapter: '', work_order: '', hangar: '', estimated_days: '', work_package: '', scheduled_date: '', program_id: '' };

export default function MaintenancePage() {
    const { hasRole, token, user } = useAuth();
    const navigate = useNavigate();
    const headers = { Authorization: `Bearer ${token}` };
    const [lineRecords, setLineRecords] = useState([]);
    const [baseRecords, setBaseRecords] = useState([]);
    const [aircraft, setAircraft] = useState([]);
    const [captainReports, setCaptainReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('line');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showLineDialog, setShowLineDialog] = useState(false);
    const [showBaseDialog, setShowBaseDialog] = useState(false);
    const [lineForm, setLineForm] = useState(emptyLine);
    const [baseForm, setBaseForm] = useState(emptyBase);
    const [actionDialog, setActionDialog] = useState(null);
    const [actionForm, setActionForm] = useState({ status: '', maintenance_notes: '', mel_reference: '', estimated_duration: '' });
    // Auditoria 3 - Item 6: programas predictivos disponibles para linkeo directo
    const [mxPrograms, setMxPrograms] = useState([]);
    const canManage = hasRole(['admin', 'maintenance']);

    const fetchData = useCallback(async () => {
        try {
            const [lineRes, baseRes, acRes] = await Promise.all([
                axios.get(`${API}/maintenance?category=line`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/maintenance?category=base`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/aircraft`, { headers }).catch(() => ({ data: [] })),
            ]);
            setLineRecords(lineRes.data);
            setBaseRecords(baseRes.data);
            setAircraft(acRes.data);
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    }, []);

    const fetchReports = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/captain-reports`, { headers });
            setCaptainReports(res.data);
        } catch { toast.error('No se pudieron cargar los reportes de capitán'); }
    }, []);

    // Auditoria 3 - Item 6: cargar programas predictivos para linkeo directo desde el form base
    const fetchPrograms = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/predictive-maintenance/status`, { headers });
            const progs = [];
            (res.data?.fleet || []).forEach(ac => {
                (ac.checks || []).forEach(c => progs.push({
                    program_id: c.program_id,
                    label: `${ac.registration} · Check ${c.check_type}`,
                    aircraft_id: ac.aircraft_id,
                    check_type: c.check_type,
                }));
            });
            setMxPrograms(progs);
        } catch { /* modulo no habilitado o sin permisos: silent */ }
    }, []);

    useEffect(() => { fetchData(); fetchReports(); fetchPrograms(); }, [fetchData, fetchReports, fetchPrograms]);

    const createLine = async () => {
        if (!lineForm.aircraft_id || !lineForm.description) { toast.error('Aeronave y descripcion requeridos'); return; }
        try {
            await axios.post(`${API}/maintenance`, { ...lineForm, maintenance_category: 'line' }, { headers });
            toast.success('Tarea de línea creada');
            setShowLineDialog(false);
            setLineForm(emptyLine);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const createBase = async () => {
        if (!baseForm.aircraft_id || !baseForm.description) { toast.error('Aeronave y descripcion requeridos'); return; }
        try {
            await axios.post(`${API}/maintenance`, {
                ...baseForm,
                maintenance_category: 'base',
                scheduled_date: baseForm.scheduled_date ? new Date(baseForm.scheduled_date).toISOString() : null,
            }, { headers });
            toast.success('Orden de mantenimiento base creada');
            setShowBaseDialog(false);
            setBaseForm(emptyBase);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await axios.put(`${API}/maintenance/${id}`, { status: newStatus }, { headers });
            toast.success('Estado actualizado');
            fetchData();
        } catch { toast.error('Error'); }
    };

    // Auditoria 3 - Item 5: eliminar registro de mantenimiento (admin/mantenimiento)
    const deleteRecord = async (id) => {
        if (!window.confirm('¿Eliminar este registro de mantenimiento? Esta acción no puede deshacerse.')) return;
        try {
            await axios.delete(`${API}/maintenance/${id}`, { headers });
            toast.success('Registro eliminado');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al eliminar');
        }
    };

    // Auditoria 7 - Item 5: exportar orden de trabajo en PDF
    const exportPDF = async (recordId) => {
        try {
            const res = await axios.get(`${API}/maintenance/${recordId}/pdf`, { responseType: 'blob', headers });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error('Error al generar PDF');
        }
    };

    // Auditoria 4 - Items 11-12: marcar como AOG (no diferible) con ETR
    const [aogDialog, setAogDialog] = useState(null);
    const [aogEtr, setAogEtr] = useState('');
    // Auditoria 6 - Item 1: convertir AOG en diferido real
    const [deferAogDialog, setDeferAogDialog] = useState(null);
    const [deferAogForm, setDeferAogForm] = useState({ mel_reference: '', category: 'B', ata_chapter: '', description: '', operational_limitations: '', station_repair: '' });
    const [savingDeferAog, setSavingDeferAog] = useState(false);

    const submitDeferAog = async () => {
        if (!deferAogDialog) return;
        const j = (deferAogForm.justification || '').trim();
        if (j.length < 20) { toast.error('Justificación obligatoria (mínimo 20 caracteres). Un AOG y un diferido son excluyentes: reclasificar solo si la clasificación inicial fue errónea.'); return; }
        if (!deferAogForm.mel_reference) { toast.error('Referencia MEL/CDL requerida'); return; }
        setSavingDeferAog(true);
        try {
            await axios.post(`${API}/maintenance/${deferAogDialog.id}/defer-aog`, deferAogForm, { headers });
            toast.success('AOG reclasificado administrativamente. Justificación guardada en la trazabilidad.');
            setDeferAogDialog(null);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al reclasificar AOG');
        } finally { setSavingDeferAog(false); }
    };

    // Auditoria 6 - Item 23: LA acción normal para cerrar un AOG (reparación completada)
    const repairComplete = async (record) => {
        if (!record) return;
        if (!window.confirm(`¿Confirmas que la reparación de "${record.description}" está completada y la aeronave puede volver al servicio?`)) return;
        try {
            await axios.post(`${API}/maintenance/${record.id}/repair-complete`, {}, { headers });
            toast.success('AOG reparado. Aeronave liberada al servicio.');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al liberar la aeronave');
        }
    };

    // Auditoria 6 (v2): actualizar ETR desde Mantenimiento (movido desde /cco)
    const [etrUpdateRecord, setEtrUpdateRecord] = useState(null);
    const [newEtrValue, setNewEtrValue] = useState('');
    const [savingEtrUpdate, setSavingEtrUpdate] = useState(false);
    const openEtrUpdate = (record) => {
        setEtrUpdateRecord(record);
        setNewEtrValue(record.aog_etr || record.etr || '');
    };
    const submitEtrUpdate = async () => {
        if (!etrUpdateRecord || !newEtrValue) { toast.error('Nueva ETR requerida'); return; }
        if (!etrUpdateRecord.aog_event_id) { toast.error('Este registro no tiene un evento AOG asociado'); return; }
        setSavingEtrUpdate(true);
        try {
            await axios.patch(`${API}/aog/${etrUpdateRecord.aog_event_id}/etr`, { etr: newEtrValue }, { headers });
            toast.success('ETR actualizada y notificada');
            setEtrUpdateRecord(null);
            setNewEtrValue('');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar ETR');
        } finally { setSavingEtrUpdate(false); }
    };

    // Auditoria 5: auto-refresh 30s de registros MX. Pausa cuando el diálogo AOG está abierto
    // para no interrumpir la edición del ETR.
    useAutoRefresh(fetchData, 30000, !aogDialog);
    const [aogRecipients, setAogRecipients] = useState({ roles: ['ccod', 'dispatcher', 'admin'], users: [] });
    const [savingAog, setSavingAog] = useState(false);

    const markAsAog = async () => {
        if (!aogDialog) return;
        if (!aogEtr) { toast.error('ETR es obligatorio'); return; }
        setSavingAog(true);
        try {
            // Stage 4 - UTCDateTimePicker ya emite ISO UTC; enviamos tal cual
            await axios.post(`${API}/maintenance/${aogDialog.id}/mark-aog`, {
                etr: aogEtr,
                work_description: aogDialog.description,
                recipients_roles: aogRecipients.roles,
                recipients_users: aogRecipients.users,
            }, { headers });
            toast.success('AOG declarado. Aviso enviado a CCO/Despacho/Rampa.');
            setAogDialog(null);
            setAogEtr('');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al declarar AOG');
        } finally {
            setSavingAog(false);
        }
    };

    const handleReportAction = async () => {
        if (!actionDialog) return;
        try {
            await axios.patch(`${API}/captain-reports/${actionDialog.id}/status`, {
                status: actionForm.status, maintenance_notes: actionForm.maintenance_notes, mel_reference: actionForm.mel_reference,
            }, { headers });
            if (actionForm.status === 'unscheduled_maintenance' && actionDialog.aircraft_id) {
                await axios.post(`${API}/maintenance`, {
                    aircraft_id: actionDialog.aircraft_id, maintenance_category: 'line',
                    maintenance_type: 'unscheduled', description: `Reporte: ${actionDialog.title} - ${actionDialog.description}`,
                    ata_chapter: actionDialog.ata_chapter || '', work_order: `WO-RPT-${actionDialog.id.slice(0, 8).toUpperCase()}`,
                }, { headers });
            }
            toast.success('Reporte actualizado');
            setActionDialog(null);
            setActionForm({ status: '', maintenance_notes: '', mel_reference: '', estimated_duration: '' });
            fetchReports(); fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const getAc = (id) => aircraft.find(a => a.id === id);
    const statusBadge = (s) => { const c = statuses.find(x => x.value === s); return <Badge className={c?.color || ''}>{c?.label || s}</Badge>; };
    const lineTypeBadge = (t) => { const c = lineTypes.find(x => x.value === t); return <Badge variant="outline" className="text-[10px]">{c?.label || t}</Badge>; };
    const baseTypeBadge = (t) => { const c = baseTypes.find(x => x.value === t); return <Badge variant="outline" className="text-[10px] font-mono">{c?.label || t}</Badge>; };

    const filterRecords = (records) => records.filter(r => {
        const ac = getAc(r.aircraft_id);
        const match = (r.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.work_order || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ac?.registration || '').toLowerCase().includes(searchTerm.toLowerCase());
        return match && (statusFilter === 'all' || r.status === statusFilter);
    });

    const lineStats = { pending: lineRecords.filter(r => r.status === 'pending').length, in_progress: lineRecords.filter(r => r.status === 'in_progress').length, completed: lineRecords.filter(r => r.status === 'completed').length };
    const baseStats = { pending: baseRecords.filter(r => r.status === 'pending').length, in_progress: baseRecords.filter(r => r.status === 'in_progress').length, completed: baseRecords.filter(r => r.status === 'completed').length };
    const openReports = captainReports.filter(r => r.status === 'open').length;

    if (loading) return <div className="space-y-4 animate-fade-in">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />)}</div>;

    const RecordCard = ({ record, isBase }) => {
        const ac = getAc(record.aircraft_id);
        return (
            <Card className={`card-aviation ${record.status === 'pending' && record.maintenance_type === 'aog' ? 'border-red-500/50' : ''}`} data-testid={`mx-card-${record.id}`}>
                <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isBase ? 'bg-violet-500/10' : 'bg-primary/10'}`}>
                                {isBase ? <Settings className="w-5 h-5 text-violet-500" /> : <Wrench className="w-5 h-5 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    {statusBadge(record.status)}
                                    {isBase ? baseTypeBadge(record.maintenance_type) : lineTypeBadge(record.maintenance_type)}
                                    {record.ata_chapter && <Badge variant="outline" className="text-[10px] font-mono">ATA {record.ata_chapter}</Badge>}
                                </div>
                                <p className="text-sm font-medium truncate">{record.description}</p>
                                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground flex-wrap">
                                    {ac && <span className="flex items-center gap-1"><Plane className="w-3 h-3" />{ac.registration}</span>}
                                    {record.work_order && <span className="font-mono">{record.work_order}</span>}
                                    {record.station && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{record.station}</span>}
                                    {record.technician && <span className="flex items-center gap-1"><User className="w-3 h-3" />{record.technician}</span>}
                                    {record.hangar && <span>Hangar: {record.hangar}</span>}
                                    {record.estimated_days && <span className="flex items-center gap-1"><Timer className="w-3 h-3" />{record.estimated_days} dias</span>}
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(record.created_at).toLocaleDateString('es')}</span>
                                </div>
                            </div>
                        </div>
                        {canManage && record.status !== 'completed' && (
                            <div className="flex flex-col gap-1 flex-shrink-0">
                                {record.status === 'pending' && (
                                    <Button size="sm" variant="outline" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px]" onClick={() => updateStatus(record.id, 'in_progress')} data-testid={`mx-start-${record.id}`}>Iniciar</Button>
                                )}
                                {record.status === 'in_progress' && (
                                    <Button size="sm" variant="outline" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-emerald-500" onClick={() => updateStatus(record.id, 'completed')} data-testid={`mx-complete-${record.id}`}>
                                        <CheckCircle className="w-3 h-3 mr-1" />Completar
                                    </Button>
                                )}
                                {/* Auditoria 6 - Item 23: LA acción NORMAL para cerrar un AOG */}
                                {record.status === 'aog' && (
                                    <Button size="sm" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] bg-emerald-500 hover:bg-emerald-600 text-white font-bold" onClick={() => repairComplete(record)} data-testid={`mx-repair-complete-${record.id}`}>
                                        <CheckCircle className="w-3 h-3 mr-1" />Reparado — Liberar A/C
                                    </Button>
                                )}
                                {/* Auditoria 6 v2: actualizar ETR (movido desde /cco) */}
                                {record.status === 'aog' && record.aog_event_id && (
                                    <Button size="sm" variant="outline" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-primary" onClick={() => openEtrUpdate(record)} data-testid={`mx-update-etr-${record.id}`}>
                                        <Clock className="w-3 h-3 mr-1" />Actualizar ETR
                                    </Button>
                                )}
                                {/* Auditoria 6 - Item 1 CORREGIDO: reclasificación administrativa (excepción, no acción normal) */}
                                {record.status === 'aog' && user?.role === 'admin' && (
                                    <Button size="sm" variant="ghost" className="h-7 md:h-9 text-[9px] md:text-[10px] md:min-w-[110px] text-amber-600 border border-dashed border-amber-600/40" onClick={() => { setDeferAogDialog(record); setDeferAogForm({ justification: '', mel_reference: '', category: 'B', ata_chapter: record.ata_chapter || '', description: record.description || '', operational_limitations: '', station_repair: '' }); }} data-testid={`mx-reclassify-aog-${record.id}`}>
                                        <FileWarning className="w-3 h-3 mr-1" />⋮ Reclasificar (admin)
                                    </Button>
                                )}
                                {record.status !== 'deferred' && record.status !== 'aog' && (
                                    <Button size="sm" variant="ghost" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-amber-500" onClick={() => updateStatus(record.id, 'deferred')}>Diferir</Button>
                                )}
                                {/* Auditoria 4 - Item 11: No diferible - AOG */}
                                {record.status !== 'aog' && !record.deferral_id && (
                                    <Button size="sm" variant="ghost" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-red-500 font-bold" onClick={() => { setAogDialog(record); setAogEtr(''); }} data-testid={`mx-aog-${record.id}`}>
                                        <AlertOctagon className="w-3 h-3 mr-1" />AOG
                                    </Button>
                                )}
                                {/* Auditoria 6 - Item 25: enlace al diferido si la tarea fue reclasificada */}
                                {record.deferral_id && (
                                    <Button size="sm" variant="outline" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-amber-500 border-amber-500/40" onClick={() => navigate(`/deferrals?id=${record.deferral_id}`)} data-testid={`mx-view-deferral-${record.id}`}>
                                        <FileWarning className="w-3 h-3 mr-1" />Ver diferido →
                                    </Button>
                                )}
                                {/* Auditoria 6 - Item 24: ocultar Eliminar si está en AOG o vino de reclasificación */}
                                {record.status !== 'aog' && !record.deferral_id && (
                                    <Button size="sm" variant="ghost" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-destructive" onClick={() => deleteRecord(record.id)} data-testid={`mx-delete-${record.id}`}>
                                        <Trash2 className="w-3 h-3 mr-1" />Eliminar
                                    </Button>
                                )}
                                {/* Auditoria 7 - Item 5: exportar OT en PDF */}
                                <Button size="sm" variant="outline" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px]" onClick={() => exportPDF(record.id)} data-testid={`mx-pdf-${record.id}`}>
                                    <FileText className="w-3 h-3 mr-1" />PDF
                                </Button>
                            </div>
                        )}
                        {canManage && record.status === 'completed' && (
                            <div className="flex flex-col gap-1 flex-shrink-0">
                                <Button size="sm" variant="outline" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px]" onClick={() => exportPDF(record.id)} data-testid={`mx-pdf-${record.id}`}>
                                    <FileText className="w-3 h-3 mr-1" />PDF
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 md:h-9 text-[10px] md:text-xs md:min-w-[110px] text-destructive" onClick={() => deleteRecord(record.id)} data-testid={`mx-delete-${record.id}`}>
                                    <Trash2 className="w-3 h-3 mr-1" />Eliminar
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="maintenance-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Gestion de Mantenimiento</h1>
                    <p className="text-muted-foreground">{lineRecords.length + baseRecords.length} registros totales</p>
                </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-xl font-bold font-mono text-amber-400">{lineStats.pending}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Linea Pend.</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-xl font-bold font-mono text-sky-400">{lineStats.in_progress}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Linea Activo</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-xl font-bold font-mono text-emerald-400">{lineStats.completed}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Linea Hecho</p>
                </CardContent></Card>
                <Card className="card-aviation border-violet-500/20"><CardContent className="p-3 text-center">
                    <p className="text-xl font-bold font-mono text-amber-400">{baseStats.pending}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Base Pend.</p>
                </CardContent></Card>
                <Card className="card-aviation border-violet-500/20"><CardContent className="p-3 text-center">
                    <p className="text-xl font-bold font-mono text-sky-400">{baseStats.in_progress}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Base Activo</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-xl font-bold font-mono text-red-400">{openReports}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Reportes Abiertos</p>
                </CardContent></Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="line" data-testid="tab-line"><Wrench className="w-3.5 h-3.5 mr-1" />Mantenimiento Linea</TabsTrigger>
                    <TabsTrigger value="base" data-testid="tab-base"><Settings className="w-3.5 h-3.5 mr-1" />Mantenimiento Base</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="tab-reports">
                        <FileText className="w-3.5 h-3.5 mr-1" />Reportes Tripulacion
                        {openReports > 0 && <Badge variant="destructive" className="ml-1.5 text-[10px] h-4 px-1">{openReports}</Badge>}
                    </TabsTrigger>
                </TabsList>

                {/* === LINE MAINTENANCE === */}
                <TabsContent value="line" className="mt-4 space-y-4">
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input placeholder="Buscar tarea de línea..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-line" />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[160px]" data-testid="filter-line-status"><SelectValue placeholder="Estado" /></SelectTrigger>
                            <SelectContent><SelectItem value="all">Todos</SelectItem>{statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {canManage && <Button className="btn-primary" onClick={() => setShowLineDialog(true)} data-testid="new-line-btn"><Plus className="w-4 h-4 mr-1" />Nueva Tarea Linea</Button>}
                    </div>
                    <div className="space-y-3">
                        {filterRecords(lineRecords).length === 0 ? (
                            <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                <p>No hay tareas de mantenimiento de linea</p>
                                <p className="text-xs mt-1">Inspecciones pre/post vuelo, troubleshooting, cambios de componentes</p>
                            </CardContent></Card>
                        ) : filterRecords(lineRecords).map(r => <RecordCard key={r.id} record={r} isBase={false} />)}
                    </div>
                </TabsContent>

                {/* === BASE MAINTENANCE === */}
                <TabsContent value="base" className="mt-4 space-y-4">
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input placeholder="Buscar orden de base..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-base" />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[160px]" data-testid="filter-base-status"><SelectValue placeholder="Estado" /></SelectTrigger>
                            <SelectContent><SelectItem value="all">Todos</SelectItem>{statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {canManage && <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setShowBaseDialog(true)} data-testid="new-base-btn"><Plus className="w-4 h-4 mr-1" />Nueva Orden Base</Button>}
                    </div>
                    <div className="space-y-3">
                        {filterRecords(baseRecords).length === 0 ? (
                            <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                                <Settings className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                <p>No hay ordenes de mantenimiento base</p>
                                <p className="text-xs mt-1">Checks A/B/C/D, overhauls, reparaciones estructurales</p>
                            </CardContent></Card>
                        ) : filterRecords(baseRecords).map(r => <RecordCard key={r.id} record={r} isBase={true} />)}
                    </div>
                </TabsContent>

                {/* === CAPTAIN REPORTS === */}
                <TabsContent value="reports" className="mt-4 space-y-4">
                    {captainReports.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No hay reportes de tripulacion</p>
                        </CardContent></Card>
                    ) : captainReports.map(rpt => (
                        <Card key={rpt.id} className={`card-aviation ${rpt.status === 'open' ? 'border-l-4 border-l-red-500' : ''}`} data-testid={`rpt-${rpt.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Badge className={rpt.status === 'open' ? 'bg-red-500/10 text-red-400' : rpt.status === 'resolved' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'} data-testid={`rpt-status-${rpt.id}`}>{({ open: 'Abierto', resolved: 'Resuelto', deferred: 'Diferido', in_review: 'En revisión', mel_applied: 'MEL aplicado' })[rpt.status] || rpt.status}</Badge>
                                            <Badge variant="outline" className="text-[10px]">{({ technical: 'Técnico', operational: 'Operacional', defect: 'Defecto', observation: 'Observación', mel: 'MEL' })[rpt.report_type] || rpt.report_type}</Badge>
                                            {rpt.ata_chapter && <Badge variant="outline" className="text-[10px] font-mono">ATA {rpt.ata_chapter}</Badge>}
                                        </div>
                                        <p className="font-semibold text-sm">{rpt.title}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{rpt.description}</p>
                                        <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                                            {rpt.flight_number && <span className="font-mono">{rpt.flight_number}</span>}
                                            {rpt.aircraft_registration && <span className="font-mono" data-testid={`rpt-aircraft-${rpt.id}`}>{rpt.aircraft_registration}</span>}
                                            <span>{new Date(rpt.created_at).toLocaleDateString('es')}</span>
                                        </div>
                                    </div>
                                    {canManage && rpt.status === 'open' && (
                                        <div className="flex flex-col gap-1.5">
                                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setActionDialog(rpt); setActionForm({ status: '', maintenance_notes: '', mel_reference: '', estimated_duration: '' }); }} data-testid={`action-rpt-${rpt.id}`}>
                                                <ShieldCheck className="w-3 h-3 mr-1" />Accion
                                            </Button>
                                            {rpt.severity === 'critical' && !rpt.deferral_id && (
                                                <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={async () => {
                                                    if (!window.confirm('Crear diferido MEL desde este reporte crítico?')) return;
                                                    try {
                                                        await axios.post(`${API}/captain-reports/${rpt.id}/create-deferral`, { category: 'B' }, { headers });
                                                        toast.success('Diferido creado');
                                                        fetchReports();
                                                    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                                                }} data-testid={`create-def-${rpt.id}`}>
                                                    Crear Diferido
                                                </Button>
                                            )}
                                            {rpt.deferral_id && (
                                                <Badge variant="outline" className="text-[10px] font-mono">Diferido creado</Badge>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </TabsContent>
            </Tabs>

            {/* Line MX Dialog */}
            <Dialog open={showLineDialog} onOpenChange={setShowLineDialog}>
                <DialogContent className="sm:max-w-[500px]" data-testid="line-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Nueva Tarea de Linea</DialogTitle><DialogDescription>Registra una tarea de mantenimiento pre o post-vuelo.</DialogDescription></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Aeronave *</Label>
                                <Select value={lineForm.aircraft_id} onValueChange={v => setLineForm({...lineForm, aircraft_id: v})}>
                                    <SelectTrigger data-testid="line-aircraft"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{aircraft.map(ac => <SelectItem key={ac.id} value={ac.id}>{ac.registration} - {ac.aircraft_type}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Tipo</Label>
                                <Select value={lineForm.maintenance_type} onValueChange={v => setLineForm({...lineForm, maintenance_type: v})}>
                                    <SelectTrigger data-testid="line-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>{lineTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5"><Label>Capitulo ATA</Label><Input placeholder="21, 32..." value={lineForm.ata_chapter} onChange={e => setLineForm({...lineForm, ata_chapter: e.target.value})} data-testid="line-ata" /></div>
                            <div className="space-y-1.5"><Label>Estacion</Label><Input placeholder="MGGT" value={lineForm.station} onChange={e => setLineForm({...lineForm, station: e.target.value.toUpperCase()})} maxLength={4} data-testid="line-station" /></div>
                            <div className="space-y-1.5"><Label>Tecnico</Label><Input placeholder="J. Perez" value={lineForm.technician} onChange={e => setLineForm({...lineForm, technician: e.target.value})} data-testid="line-tech" /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Orden de Trabajo</Label><Input placeholder="WO-12345" value={lineForm.work_order} onChange={e => setLineForm({...lineForm, work_order: e.target.value})} data-testid="line-wo" /></div>
                        <div className="space-y-1.5"><Label>Descripcion *</Label><Textarea placeholder="Describe la tarea: que se encontro, que se hizo..." value={lineForm.description} onChange={e => setLineForm({...lineForm, description: e.target.value})} rows={3} data-testid="line-desc" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLineDialog(false)}>Cancelar</Button>
                        <Button onClick={createLine} className="btn-primary" data-testid="submit-line"><Wrench className="w-4 h-4 mr-1" />Crear Tarea</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Base MX Dialog */}
            <Dialog open={showBaseDialog} onOpenChange={setShowBaseDialog}>
                <DialogContent className="sm:max-w-[500px]" data-testid="base-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Nueva Orden de Mantenimiento Base</DialogTitle><DialogDescription>Programa un check periodico (A/B/C/D) o inspeccion mayor.</DialogDescription></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Aeronave *</Label>
                                <Select value={baseForm.aircraft_id} onValueChange={v => setBaseForm({...baseForm, aircraft_id: v})}>
                                    <SelectTrigger data-testid="base-aircraft"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{aircraft.map(ac => <SelectItem key={ac.id} value={ac.id}>{ac.registration} - {ac.aircraft_type}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Tipo de Check</Label>
                                <Select value={baseForm.maintenance_type} onValueChange={v => setBaseForm({...baseForm, maintenance_type: v})}>
                                    <SelectTrigger data-testid="base-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>{baseTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5"><Label>Capitulo ATA</Label><Input placeholder="21, 32..." value={baseForm.ata_chapter} onChange={e => setBaseForm({...baseForm, ata_chapter: e.target.value})} data-testid="base-ata" /></div>
                            <div className="space-y-1.5"><Label>Hangar</Label><Input placeholder="Hangar 1" value={baseForm.hangar} onChange={e => setBaseForm({...baseForm, hangar: e.target.value})} data-testid="base-hangar" /></div>
                            <div className="space-y-1.5"><Label>Dias Est.</Label><Input type="number" placeholder="15" value={baseForm.estimated_days} onChange={e => setBaseForm({...baseForm, estimated_days: e.target.value})} data-testid="base-days" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Orden de Trabajo</Label><Input placeholder="WO-BASE-001" value={baseForm.work_order} onChange={e => setBaseForm({...baseForm, work_order: e.target.value})} data-testid="base-wo" /></div>
                            <div className="space-y-1.5"><Label>Fecha Programada</Label><Input type="date" value={baseForm.scheduled_date} onChange={e => setBaseForm({...baseForm, scheduled_date: e.target.value})} data-testid="base-date" /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Paquete de Trabajo</Label><Input placeholder="WP-C-CHECK-2026" value={baseForm.work_package} onChange={e => setBaseForm({...baseForm, work_package: e.target.value})} data-testid="base-wp" /></div>
                        {/* Auditoria 3 - Item 6: seleccionar programa predictivo asociado para sync directo (opcional) */}
                        <div className="space-y-1.5">
                            <Label>Programa Predictivo Asociado (opcional)</Label>
                            <Select value={baseForm.program_id || 'none'} onValueChange={v => setBaseForm({...baseForm, program_id: v === 'none' ? '' : v})}>
                                <SelectTrigger data-testid="base-program"><SelectValue placeholder="Seleccionar programa (opcional)" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Ninguno (sync automatico por check_type)</SelectItem>
                                    {mxPrograms.filter(p => !baseForm.aircraft_id || p.aircraft_id === baseForm.aircraft_id).map(p => (
                                        <SelectItem key={p.program_id} value={p.program_id}>{p.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[10px] text-muted-foreground">Al completar la orden se sincronizara este programa por ID (sin depender del texto).</p>
                        </div>
                        <div className="space-y-1.5"><Label>Descripcion *</Label><Textarea placeholder="Descripcion del trabajo de base..." value={baseForm.description} onChange={e => setBaseForm({...baseForm, description: e.target.value})} rows={3} data-testid="base-desc" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBaseDialog(false)}>Cancelar</Button>
                        <Button onClick={createBase} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="submit-base"><Settings className="w-4 h-4 mr-1" />Crear Orden</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Report Action Dialog */}
            <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
                <DialogContent className="sm:max-w-[420px]" data-testid="report-action-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Accion sobre Reporte</DialogTitle><DialogDescription>Actualiza el estado y agrega notas de mantenimiento sobre el reporte del capitan.</DialogDescription></DialogHeader>
                    {actionDialog && (
                        <div className="space-y-3">
                            <div className="p-2 bg-muted/30 rounded text-xs"><strong>{actionDialog.title}</strong><br /><span className="text-muted-foreground">{actionDialog.description}</span></div>
                            <div className="space-y-1.5">
                                <Label>Accion</Label>
                                <Select value={actionForm.status} onValueChange={v => setActionForm({...actionForm, status: v})}>
                                    <SelectTrigger data-testid="rpt-action-select"><SelectValue placeholder="Seleccionar acción" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="resolved">Resuelto</SelectItem>
                                        <SelectItem value="deferred">Diferido (MEL)</SelectItem>
                                        <SelectItem value="mel_applied">MEL Aplicado</SelectItem>
                                        <SelectItem value="in_review">En Revision</SelectItem>
                                        <SelectItem value="unscheduled_maintenance">Crear Tarea Mtto Linea</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {(actionForm.status === 'deferred' || actionForm.status === 'mel_applied') && (
                                <div className="space-y-1.5"><Label>Referencia MEL</Label><Input value={actionForm.mel_reference} onChange={e => setActionForm({...actionForm, mel_reference: e.target.value})} placeholder="MEL 21-51-01" data-testid="rpt-mel-ref" /></div>
                            )}
                            <div className="space-y-1.5"><Label>Notas de Mantenimiento</Label><Textarea value={actionForm.maintenance_notes} onChange={e => setActionForm({...actionForm, maintenance_notes: e.target.value})} rows={2} data-testid="rpt-notes" /></div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActionDialog(null)}>Cancelar</Button>
                        <Button onClick={handleReportAction} disabled={!actionForm.status} className="btn-primary" data-testid="submit-rpt-action"><Send className="w-4 h-4 mr-1" />Aplicar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Auditoria 4 - Items 11-12: dialogo AOG con ETR y destinatarios */}
            <Dialog open={!!aogDialog} onOpenChange={(o) => { if (!o) setAogDialog(null); }}>
                <DialogContent className="sm:max-w-[520px]" data-testid="aog-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2 text-red-500">
                            <AlertOctagon className="w-5 h-5" />
                            Declarar AOG — No Diferible
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs">
                            <p><strong>Atención:</strong> La aeronave pasará a estado <strong>AOG</strong> inmediatamente. Los vuelos afectados deben ser gestionados por CCO. Todos los horarios en <strong>UTC</strong>.</p>
                        </div>
                        <div className="space-y-1.5">
                            <Label>ETR — Fecha/hora estimada de reparación (UTC) *</Label>
                            <UTCDateTimePicker value={aogEtr} onChange={setAogEtr} testid="aog-etr" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Destinatarios del aviso (roles)</Label>
                            <div className="flex flex-wrap gap-2">
                                {['ccod', 'dispatcher', 'admin', 'gerencia', 'ramp'].map(r => {
                                    const active = aogRecipients.roles.includes(r);
                                    return (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => setAogRecipients(prev => ({ ...prev, roles: active ? prev.roles.filter(x => x !== r) : [...prev.roles, r] }))}
                                            className={`px-3 py-1 rounded-full text-xs uppercase font-bold border ${active ? 'bg-red-500 text-white border-red-500' : 'bg-transparent text-muted-foreground border-border'}`}
                                            data-testid={`aog-role-${r}`}
                                        >
                                            {r}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAogDialog(null)}>Cancelar</Button>
                        <Button onClick={markAsAog} disabled={savingAog || !aogEtr} className="bg-red-500 hover:bg-red-600 text-white" data-testid="aog-confirm">
                            {savingAog ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <AlertOctagon className="w-4 h-4 mr-1" />}
                            Declarar AOG
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Auditoria 6 - Item 1 CORREGIDO: Reclasificación administrativa (excepción, no acción normal) */}
            <Dialog open={!!deferAogDialog} onOpenChange={(o) => { if (!o) setDeferAogDialog(null); }}>
                <DialogContent className="sm:max-w-[560px] border-amber-500/40">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] uppercase text-lg flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                            Reclasificación Administrativa — AOG → Diferido
                        </DialogTitle>
                        <DialogDescription className="text-amber-700/90">
                            ⚠️ Un AOG y un diferido son <b>desenlaces excluyentes</b>. Usa esta acción SOLO si la clasificación inicial fue errónea (el ítem era diferible desde el inicio). Queda registrada con severidad crítica en auditoría y notificaciones.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-amber-700">Justificación de la reclasificación (mínimo 20 caracteres) *</Label>
                            <textarea
                                className="w-full min-h-[80px] rounded-md border border-amber-500/40 bg-background px-3 py-2 text-sm"
                                value={deferAogForm.justification || ''}
                                onChange={e => setDeferAogForm({ ...deferAogForm, justification: e.target.value })}
                                placeholder="Ej: El ítem 29-10-01 sí está en la MEL como diferible cat B; se marcó AOG por error de lectura del catálogo. Se reclasifica con placard hasta reparación en base."
                                data-testid="defer-aog-justification"
                            />
                            <p className="text-[10px] text-muted-foreground">{(deferAogForm.justification || '').trim().length} / 20 caracteres mínimo</p>
                        </div>
                        <div className="grid grid-cols-[1fr_120px] gap-3">
                            <div className="space-y-1.5">
                                <Label>Referencia MEL/CDL *</Label>
                                <Input value={deferAogForm.mel_reference} onChange={e => setDeferAogForm({ ...deferAogForm, mel_reference: e.target.value })} placeholder="Ej. 24-01-01A" data-testid="defer-aog-mel" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Categoría</Label>
                                <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={deferAogForm.category} onChange={e => setDeferAogForm({ ...deferAogForm, category: e.target.value })} data-testid="defer-aog-category">
                                    <option value="A">A (3d)</option>
                                    <option value="B">B (3d)</option>
                                    <option value="C">C (10d)</option>
                                    <option value="D">D (120d)</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Capítulo ATA</Label>
                                <Input value={deferAogForm.ata_chapter} onChange={e => setDeferAogForm({ ...deferAogForm, ata_chapter: e.target.value })} placeholder="Ej. 29-10" data-testid="defer-aog-ata" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Estación de reparación</Label>
                                <Input value={deferAogForm.station_repair} onChange={e => setDeferAogForm({ ...deferAogForm, station_repair: e.target.value })} placeholder="Ej. GUA" data-testid="defer-aog-station" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Descripción</Label>
                            <Input value={deferAogForm.description} onChange={e => setDeferAogForm({ ...deferAogForm, description: e.target.value })} data-testid="defer-aog-description" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Limitaciones operacionales</Label>
                            <Input value={deferAogForm.operational_limitations} onChange={e => setDeferAogForm({ ...deferAogForm, operational_limitations: e.target.value })} placeholder="Ej. No RVSM, altitud máx FL200" data-testid="defer-aog-limitations" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeferAogDialog(null)}>Cancelar</Button>
                        <Button onClick={submitDeferAog} disabled={savingDeferAog || !deferAogForm.mel_reference || (deferAogForm.justification || '').trim().length < 20} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="defer-aog-confirm">
                            {savingDeferAog ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                            Reclasificar Administrativamente
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Auditoria 6 v2: Dialog Actualizar ETR (movido desde /cco) */}
            <Dialog open={!!etrUpdateRecord} onOpenChange={(o) => { if (!o) { setEtrUpdateRecord(null); setNewEtrValue(''); } }}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] uppercase text-lg flex items-center gap-2">
                            <Clock className="w-4 h-4 text-primary" />
                            Actualizar ETR
                        </DialogTitle>
                        <DialogDescription>
                            Cambia la hora estimada de retorno al servicio de este evento AOG. Se emitirá un aviso a los roles configurados (CCOD, Despacho, Admin).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nueva ETR (UTC)</Label>
                        <UTCDateTimePicker value={newEtrValue} onChange={setNewEtrValue} testid="mx-etr-update" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setEtrUpdateRecord(null); setNewEtrValue(''); }}>Cancelar</Button>
                        <Button className="btn-primary" onClick={submitEtrUpdate} disabled={savingEtrUpdate || !newEtrValue} data-testid="mx-etr-update-save">
                            {savingEtrUpdate ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />}
                            Guardar ETR
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
